import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';

import google from 'lib/AnalyticsGDM/services/google';
import { isScriptLoadedById } from 'lib/AnalyticsGDM/utils';

import { LEGAL_PAGE_ROUTES, RESOURCES_MAPPINGS } from '../constants';
import { getMonetizationDataLayerFields } from '../monetization';

export const gtm = {
  sendEvent: google.sendEvent,
  // @ts-ignore
  sendPage: google.sendPage,
};

const getAuthDataLayerFields = (
  g2UserId,
  loginStatus,
  shouldClearG2UserId
) => ({
  ...(g2UserId ? { g2UserId } : shouldClearG2UserId && { g2UserId: null }),
  ...(shouldClearG2UserId && { event: 'g2_user_id_cleared' }),
  ...(loginStatus && { loginStatus }),
});

const appendPageContextDataLayerFields = (
  initialDataLayer,
  experiment,
  route
) => {
  if (experiment?.User_Information_GAExperimentId) {
    initialDataLayer.push({
      User_Information_GAExperimentId:
        experiment.User_Information_GAExperimentId,
      User_Information_GAExperimentUserId:
        experiment.User_Information_GAExperimentUserId,
    });
  }
  if (
    LEGAL_PAGE_ROUTES.includes(route) ||
    Object.keys(RESOURCES_MAPPINGS).includes(route)
  ) {
    if (LEGAL_PAGE_ROUTES.includes(route)) {
      initialDataLayer.push({ contentGroupGA4: 'Other/Legal' });
    } else {
      initialDataLayer.push({ contentGroupGA4: RESOURCES_MAPPINGS[route] });
    }
  }
};

const useGoogleTagManager = (config, head, countryCode, route, experiment) => {
  const { data: session, status } = useSession();
  const previousG2UserId = useRef();
  const g2UserId = session?.user?.externalId || undefined;
  const loginStatus =
    status === 'loading'
      ? undefined
      : status === 'authenticated'
        ? 'logged_in'
        : 'logged_out';

  useEffect(() => {
    if (status === 'loading') return;

    const safeHead = head || {};
    const initialDataLayer = [...(safeHead.data_layer || [])];
    const shouldClearG2UserId = Boolean(!g2UserId && previousG2UserId.current);
    const authDataLayerFields = getAuthDataLayerFields(
      g2UserId,
      loginStatus,
      shouldClearG2UserId
    );
    const shouldAnnounceG2UserId = Boolean(
      g2UserId && g2UserId !== previousG2UserId.current
    );
    const authDataLayerEventFields = shouldAnnounceG2UserId
      ? { ...authDataLayerFields, event: 'g2_user_id_ready' }
      : authDataLayerFields;
    const monetizationFields = getMonetizationDataLayerFields({
      head: safeHead,
      countryCode,
      route,
    });
    initialDataLayer.push(authDataLayerEventFields);
    // Land monetization fields before the gtm.js marker pushed below: GTM
    // evaluates the "All Pages" trigger (and therefore the GA4 page_view
    // tag) while replaying the dataLayer up to and including that marker,
    // so on the very first page load monetization_type must already be
    // present in initialDataLayer or the resulting page_view never carries
    // it - pushing it only afterwards (see google.send below) is too late
    // for this first pass.
    initialDataLayer.push(monetizationFields);

    // The tracking-cloudflare-worker inlines its UUIDv7 request id as
    // `window.xRequestId` before any React code runs. Push it into the
    // same pre-`gtm.js` hop as the monetization fields so the GA4 Config
    // tag can pick it up as `cf_request_id` on every downstream event.
    // See ULGOA-165.
    if (
      typeof window !== 'undefined' &&
      typeof window.xRequestId === 'string'
    ) {
      initialDataLayer.push({ cf_request_id: window.xRequestId });
    }

    appendPageContextDataLayerFields(initialDataLayer, experiment, route);
    const wasGtmLoaded = isScriptLoadedById('gtag-script');
    google.init({
      gtm: {
        id: config?.id,
        initialDataLayer: [
          ...initialDataLayer,
          { countryCode },
          {
            'gtm.start': Date.now(),
            event: 'gtm.js',
          },
        ],
      },
    });

    // google.init is a no-op once gtag-script is loaded, so on every SPA
    // navigation after the first its initialDataLayer never reaches
    // window.dataLayer. Push the monetization fields again outside that
    // guard so navigations after the first still refresh with the CURRENT
    // page's values (the push above only lands on the very first page load).
    google.send(monetizationFields);
    google.send(wasGtmLoaded ? authDataLayerEventFields : authDataLayerFields);
    google.send({ event: 'monetization_type' });

    previousG2UserId.current = g2UserId;
  }, [
    config,
    route,
    countryCode,
    head,
    experiment,
    g2UserId,
    loginStatus,
    status,
  ]);
};

export default useGoogleTagManager;
